/**
 * Date and time formatting utilities for activity details
 */

/**
 * Detects an ISO date-time string carrying a timezone (trailing Z or ±HH:MM offset)
 */
const hasTimezoneOffset = (value: string): boolean =>
    /T.*(Z|[+-]\d{2}:\d{2})$/.test(value);

/**
 * Formats an ISO timestamp as a full date-time in the viewer's browser timezone,
 * using a 24-hour clock (e.g. "22/06/2026, 19:42:57")
 */
export const formatDateTimeLocal = (
    value: string | undefined,
    locale?: string
): string => {
    if (!value) {
        return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).format(date);
};

/**
 * Extracts and formats date from ISO string
 * @param value - ISO date string (e.g., "2026-02-12T10:30:00")
 * @param locale - Locale for formatting
 * @returns Formatted date string or null
 */
export const formatDateOnly = (
    value: string,
    locale: string
): string | null => {
    const dateFormatter = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

    if (hasTimezoneOffset(value)) {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
            return dateFormatter.format(date);
        }
    }

    const match = value.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return null;
    }
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));

    return dateFormatter.format(date);
};

/**
 * Extracts time (HH:mm) from ISO string
 * @param value - ISO date string with time
 * @param locale - Locale for formatting timezone-aware values
 * @returns Time string or null
 */
export const formatTimeOnly = (
    value: string,
    locale?: string
): string | null => {
    if (hasTimezoneOffset(value)) {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
            return new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(date);
        }
    }

    const match = value.match(/[T ](\d{2}:\d{2})/);
    return match ? match[1] : null;
};

/**
 * Result of formatting a date/time value
 */
export interface FormattedDateTime {
    dateLabel: string;
    timeLabel: string | null;
}

/**
 * Formats a date-time string into separate date and time labels
 */
export const formatDateTimeValue = (
    rawValue: unknown,
    locale: string
): FormattedDateTime | null => {
    if (typeof rawValue !== 'string') {
        return null;
    }
    const dateLabel = formatDateOnly(rawValue, locale);
    if (!dateLabel) {
        return null;
    }
    const timeLabel = formatTimeOnly(rawValue, locale);

    return {
        dateLabel,
        timeLabel
    };
};

/**
 * Result of formatting a date range
 */
export interface FormattedDateRange {
    dateLabel: string | null;
    timeLabel: string | null;
}

/**
 * Formats visit date ranges from various input formats
 * Handles slash-separated, dash-separated, and object formats
 *
 * @param rawValue - Raw value (string, array, or object)
 * @param locale - Locale for date formatting
 * @returns Formatted date range or null
 */
export const formatVisitDateRange = (
    rawValue: unknown,
    locale: string
): FormattedDateRange | null => {
    let startRaw: string | undefined;
    let endRaw: string | undefined;

    // Handle string format
    if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (!trimmed) {
            return null;
        }

        // Handle slash-separated format (e.g., "2026-01-01 / 2026-01-02")
        if (trimmed.includes('/')) {
            const parts = trimmed
                .split('/')
                .map((part) => part.trim())
                .filter(Boolean);
            const hasDate = (value: string) => /\d{4}-\d{2}-\d{2}/.test(value);

            if (parts.length >= 2 && hasDate(parts[0]) && hasDate(parts[1])) {
                startRaw = parts[0];
                endRaw = parts[1];
            }
        }
        // Handle hyphen-separated format (e.g., "2026-01-01 - 2026-01-02")
        else if (trimmed.includes(' - ')) {
            const parts = trimmed.split(' - ').map((part) => part.trim());
            startRaw = parts[0];
            endRaw = parts[1];
        }
        // Handle en-dash format (e.g., "2026-01-01 – 2026-01-02")
        else if (trimmed.includes(' – ')) {
            const parts = trimmed.split(' – ').map((part) => part.trim());
            startRaw = parts[0];
            endRaw = parts[1];
        }
        // Fallback: extract all dates with regex
        else {
            const dates = Array.from(
                trimmed.matchAll(/\d{4}-\d{2}-\d{2}/g)
            ).map((match) => match[0]);
            startRaw = dates[0] ?? trimmed;
            endRaw = dates[1];
        }
    }
    // Handle array format
    else if (Array.isArray(rawValue)) {
        startRaw = typeof rawValue[0] === 'string' ? rawValue[0] : undefined;
        endRaw = typeof rawValue[1] === 'string' ? rawValue[1] : undefined;
    }
    // Handle object format
    else if (rawValue && typeof rawValue === 'object') {
        const range = rawValue as Record<string, unknown>;
        const startCandidate =
            range.start ?? range.from ?? range.startDate ?? range.fromDate;
        const endCandidate =
            range.end ?? range.to ?? range.endDate ?? range.toDate;
        startRaw =
            typeof startCandidate === 'string' ? startCandidate : undefined;
        endRaw = typeof endCandidate === 'string' ? endCandidate : undefined;
    }

    // Format the extracted values
    const start = startRaw ? formatDateOnly(startRaw, locale) : null;
    const end = endRaw ? formatDateOnly(endRaw, locale) : null;
    const startTime = startRaw ? formatTimeOnly(startRaw, locale) : null;
    const endTime = endRaw ? formatTimeOnly(endRaw, locale) : null;

    let dateLabel: string | null = null;
    let timeLabel: string | null = null;

    // Build date label
    if (start && end) {
        dateLabel = `${start} – ${end}`;
    } else if (start) {
        dateLabel = `From ${start}`;
    } else if (end) {
        dateLabel = `Until ${end}`;
    }

    // Build time label
    if (startTime && endTime) {
        timeLabel = `${startTime} – ${endTime}`;
    } else if (startTime) {
        timeLabel = `From ${startTime}`;
    } else if (endTime) {
        timeLabel = `Until ${endTime}`;
    }

    if (!dateLabel && !timeLabel) {
        return null;
    }

    return {
        dateLabel,
        timeLabel
    };
};

/**
 * Checks if a key represents a date range field
 */
export const isRangeKey = (key: string): boolean => {
    const keyLower = key.toLowerCase();
    return (
        keyLower.includes('range') &&
        (keyLower.includes('date') ||
            keyLower.includes('time') ||
            keyLower.includes('visit'))
    );
};
